import { useState, useRef, useEffect } from "react";
import { Printer } from "lucide-react";
import { apiFetch } from "../utils/api";

const paperSizes: Record<string, string> = {
  A4: "A4",
  Letter: "letter",
  Legal: "legal",
};

interface AdaptiveChatData {
  name: string;
  email: string;
  phone: string;
  organizationType: string;
  service: string;

  // Adaptive fields
  trainingCategory?: string;
  participantCount?: string;
  trainingMode?: string;

  itRequirement?: string;
  platformType?: string;
  budgetRange?: string;
  timeline?: string;

  rndDomain?: string;
  hardwareStage?: string;

  automationTarget?: string;
  operatingEnvironment?: string;

  securityNeed?: string;
  facilityScale?: string;

  customScopeCategory?: string;
  message: string;
}

const initialChatForm: AdaptiveChatData = {
  name: "",
  email: "",
  phone: "",
  organizationType: "Startup / Small Business",
  service: "Technology Training & Education",
  trainingCategory: "Python Programming & Automation",
  participantCount: "Individual (1 Person)",
  trainingMode: "Online Live Interactive",
  itRequirement: "Custom Software Development",
  budgetRange: "₹25,000 – ₹50,000 (Standard)",
  timeline: "1 Month",
  platformType: "Multi-Tenant SaaS Product",
  rndDomain: "Applied Hardware Prototyping",
  hardwareStage: "Concept & Feasibility Study",
  automationTarget: "Intelligent Robotics Assembly",
  operatingEnvironment: "Commercial Facility",
  securityNeed: "IP Surveillance & CCTV System",
  facilityScale: "Small Office / Retail",
  customScopeCategory: "Custom Hardware + Software Hybrid",
  message: "",
};

export function FloatingWidget({ currentPath }: { currentPath: string }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [paperSize, setPaperSize] = useState("A4");
  const widgetRef = useRef<HTMLDivElement>(null);

  // Chatbot State
  const [form, setForm] = useState<AdaptiveChatData>(initialChatForm);
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [leadId, setLeadId] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const isDocumentPage = [
    "/pricing",
    "/terms",
    "/amc",
    "/privacy",
    "/data-storage",
  ].includes(currentPath);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (widgetRef.current && !widgetRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const update = (field: keyof AdaptiveChatData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handlePrint = () => {
    const existing = document.getElementById("print-page-size");
    if (existing) existing.remove();

    const style = document.createElement("style");
    style.id = "print-page-size";
    style.textContent = `@page { size: ${paperSizes[paperSize]}; margin: 12mm 15mm; }`;
    document.head.appendChild(style);

    setMenuOpen(false);
    setTimeout(() => window.print(), 150);
  };

  const handleLeadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setErrorMsg("Full Name, Email, and Message details are required.");
      return;
    }

    setStatus("sending");
    setErrorMsg("");

    try {
      const res = await apiFetch("/api/contact", {
        method: "POST",
        body: JSON.stringify(form),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Failed to submit inquiry.");
      }

      const generatedId = `RADIE-${Math.floor(100000 + Math.random() * 900000)}`;
      setLeadId(generatedId);
      setStatus("sent");
    } catch (err: any) {
      setErrorMsg(err.message || "Something went wrong. Please try again.");
      setStatus("error");
    }
  };

  const buildWhatsappText = () => {
    let summary = `*RADIE CHAT INQUIRY [${leadId || "DRAFT"}]*\nName: ${form.name || "N/A"}\nEmail: ${form.email || "N/A"}\nPhone: ${form.phone || "N/A"}\nOrganization: ${form.organizationType}\nService: ${form.service}\n`;
    if (form.service === "Technology Training & Education") {
      summary += `Course: ${form.trainingCategory}\nParticipants: ${form.participantCount}\nMode: ${form.trainingMode}\n`;
    } else if (form.service === "IT & ITeS Services") {
      summary += `Requirement: ${form.itRequirement}\nBudget: ${form.budgetRange}\nTimeline: ${form.timeline}\n`;
    } else if (form.service === "SaaS & Digital Platforms") {
      summary += `Platform: ${form.platformType}\nBudget: ${form.budgetRange}\nTimeline: ${form.timeline}\n`;
    } else if (form.service === "Research & Development (Hardware/Software)") {
      summary += `Focus: ${form.rndDomain}\nStage: ${form.hardwareStage}\n`;
    } else if (form.service === "Robotics & Automation Systems") {
      summary += `Target: ${form.automationTarget}\nEnvironment: ${form.operatingEnvironment}\n`;
    } else if (form.service === "Security & Surveillance Systems") {
      summary += `Need: ${form.securityNeed}\nScale: ${form.facilityScale}\n`;
    } else if (form.service === "Custom Engineering Requirement") {
      summary += `Scope: ${form.customScopeCategory}\n`;
    }
    summary += `\nDetails:\n${form.message || "General inquiry"}`;

    return `https://wa.me/919567551437?text=${encodeURIComponent(summary)}`;
  };

  // Adaptive condition helpers
  const isTraining = form.service === "Technology Training & Education";
  const isIT = form.service === "IT & ITeS Services";
  const isSaaS = form.service === "SaaS & Digital Platforms";
  const isRnD = form.service === "Research & Development (Hardware/Software)";
  const isRobotics = form.service === "Robotics & Automation Systems";
  const isSecurity = form.service === "Security & Surveillance Systems";
  const isCustom = form.service === "Custom Engineering Requirement";

  return (
    <div
      className="no-print fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3"
      ref={widgetRef}
    >
      {/* Interactive Radie AI & Technical Assistant Chat Drawer */}
      {chatOpen && (
        <div className="bg-[#101c2a] text-white rounded-2xl shadow-2xl border border-white/20 w-[94vw] sm:w-[420px] overflow-hidden mb-2 animate-in">
          {/* Chat Header */}
          <div className="bg-gradient-to-r from-brand-dark via-brand to-brand-dark p-4 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-10 h-10 rounded-full bg-primary/20 border border-primary/40 text-primary flex items-center justify-center font-extrabold text-sm shadow-md">
                  R
                </div>
                <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-emerald-400 border-2 border-brand-dark animate-pulse"></span>
              </div>
              <div>
                <div className="flex items-center gap-1.5">
                  <h4 className="text-sm font-bold text-white">Radie</h4>
                  <span className="bg-primary/20 text-primary border border-primary/30 text-[10px] font-semibold px-2 py-0.5 rounded-full">
                    Assistant
                  </span>
                </div>
                <p className="text-[11px] text-slate-300">RADIECE Technical & Lead Desk</p>
              </div>
            </div>
            <button
              onClick={() => {
                setChatOpen(false);
                setStatus("idle");
              }}
              className="text-slate-400 hover:text-white text-lg font-bold p-1 transition-colors cursor-pointer"
            >
              ✕
            </button>
          </div>

          {/* Chat Body */}
          <div className="p-4 sm:p-5 max-h-[75vh] overflow-y-auto bg-brand-dark/95 space-y-4">
            {status === "sent" ? (
              <div className="text-center py-8 space-y-4">
                <div className="w-14 h-14 bg-emerald-500/20 text-emerald-400 rounded-full flex items-center justify-center mx-auto text-2xl font-bold border border-emerald-500/30">
                  ✓
                </div>
                <h3 className="text-xl font-extrabold text-white">Radie Received Your Inquiry!</h3>
                <p className="text-xs text-slate-300 leading-relaxed max-w-xs mx-auto">
                  Your ticket reference is <strong className="text-primary font-mono text-sm">{leadId}</strong>. Our engineering leads will review your parameters and follow up shortly.
                </p>

                <div className="pt-3 space-y-2.5">
                  <a
                    href={buildWhatsappText()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-3 px-4 rounded-xl text-xs transition-all shadow-lg shadow-emerald-900/30"
                  >
                    Transfer Inquiry to WhatsApp →
                  </a>
                  <button
                    onClick={() => {
                      setStatus("idle");
                      setForm(initialChatForm);
                    }}
                    className="w-full text-xs text-slate-400 hover:text-white py-1.5 transition-colors cursor-pointer"
                  >
                    Submit Another Inquiry
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleLeadSubmit} className="space-y-4">
                {/* Greeting Bubble */}
                <div className="bg-white/5 border border-white/10 rounded-xl p-3 text-xs text-slate-200 leading-relaxed flex items-start gap-2.5">
                  <span className="text-base shrink-0">🤖</span>
                  <div>
                    <strong>Hi! I'm Radie.</strong> Select your service domain below — this form dynamically adapts to gather precise technical parameters for rapid response.
                  </div>
                </div>

                {/* 1. Shared Fields: Name & Email */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-bold text-slate-300 uppercase tracking-wider mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Jane Doe"
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-xs text-white placeholder-slate-400 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold text-slate-300 uppercase tracking-wider mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={update("email")}
                      placeholder="jane@example.com"
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-xs text-white placeholder-slate-400 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
                    />
                  </div>
                </div>

                {/* 2. Shared Fields: Phone & Org */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-bold text-slate-300 uppercase tracking-wider mb-1">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="+91 98765 43210"
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-xs text-white placeholder-slate-400 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold text-slate-300 uppercase tracking-wider mb-1">
                      Organization
                    </label>
                    <select
                      value={form.organizationType}
                      onChange={update("organizationType")}
                      className="w-full px-3 py-2 bg-[#101c2a] border border-white/20 rounded-lg text-xs text-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                    >
                      <option value="Startup / Small Business">Startup / Small Business</option>
                      <option value="SME / Growing Business">SME / Growing Business</option>
                      <option value="Enterprise / Corporate">Enterprise / Corporate</option>
                      <option value="Educational Institution">Educational Institution</option>
                      <option value="Government / PSU">Government / PSU</option>
                      <option value="Individual Learner">Individual Learner</option>
                    </select>
                  </div>
                </div>

                {/* 3. Primary Service Selection (Adaptive Trigger) */}
                <div>
                  <label className="block text-[11px] font-bold text-primary uppercase tracking-wider mb-1">
                    Select Service Area *
                  </label>
                  <select
                    value={form.service}
                    onChange={update("service")}
                    className="w-full px-3 py-2.5 bg-[#101c2a] border border-primary/50 rounded-lg text-xs font-bold text-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                  >
                    <option value="Technology Training & Education">Technology Training & Education</option>
                    <option value="IT & ITeS Services">IT & ITeS Services</option>
                    <option value="SaaS & Digital Platforms">SaaS & Digital Platforms</option>
                    <option value="Research & Development (Hardware/Software)">Research & Development</option>
                    <option value="Robotics & Automation Systems">Robotics & Automation</option>
                    <option value="Security & Surveillance Systems">Security & Surveillance</option>
                    <option value="Custom Engineering Requirement">Custom Engineering Requirement</option>
                  </select>
                </div>

                {/* 4. ADAPTIVE CONTEXTUAL SUB-FORM FIELDS */}

                {/* Training Adaptive Sub-Form */}
                {isTraining && (
                  <div className="p-3.5 bg-white/5 border border-white/10 rounded-xl space-y-3 animate-fadeIn">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-primary">
                      🎓 Course Specification
                    </p>
                    <div>
                      <label className="block text-[11px] text-slate-300 mb-1">Select Course *</label>
                      <select
                        value={form.trainingCategory}
                        onChange={update("trainingCategory")}
                        className="w-full px-3 py-1.5 bg-[#101c2a] border border-white/20 rounded-lg text-xs text-white focus:border-primary"
                      >
                        <option value="Python Full-Stack Web Development">Python Full-Stack Web Development</option>
                        <option value="MERN Stack Web Development">MERN Stack Web Development</option>
                        <option value="MEAN Stack Web Development">MEAN Stack Web Development</option>
                        <option value="Software Testing & Quality Assurance">Software Testing & QA</option>
                        <option value="Python Programming & Automation">Python Programming & Automation</option>
                        <option value="Data Science & Machine Learning">Data Science & Machine Learning</option>
                        <option value="Modern React & Next.js">Modern React & Next.js</option>
                        <option value="Full-Stack Web Engineering">Full-Stack Web Engineering</option>
                        <option value="Performance Digital Marketing">Performance Digital Marketing</option>
                        <option value="AI & Prompt Engineering">AI & Prompt Engineering</option>
                        <option value="Cyber Security & Ethical Hacking">Cyber Security & Ethical Hacking</option>
                        <option value="Embedded Systems & IoT Engineering">Embedded Systems & IoT Engineering</option>
                        <option value="Custom / Unlisted Subject (Specify below)">Custom / Unlisted Subject (Specify below)</option>
                        <option value="Corporate Technology Training">Corporate Technology Training</option>
                      </select>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-[11px] text-slate-300 mb-1">Mode</label>
                        <select
                          value={form.trainingMode}
                          onChange={update("trainingMode")}
                          className="w-full px-2.5 py-1.5 bg-[#101c2a] border border-white/20 rounded-lg text-xs text-white focus:border-primary"
                        >
                          <option value="Online Live Interactive">Online Live</option>
                          <option value="Classroom / In-Person">In-Person</option>
                          <option value="Self-Paced / Hybrid">Hybrid</option>
                          <option value="Corporate On-Site Training">On-Site Training</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-[11px] text-slate-300 mb-1">Participants</label>
                        <select
                          value={form.participantCount}
                          onChange={update("participantCount")}
                          className="w-full px-2.5 py-1.5 bg-[#101c2a] border border-white/20 rounded-lg text-xs text-white focus:border-primary"
                        >
                          <option value="Individual (1 Person)">1 Person</option>
                          <option value="Small Group (2-5 Persons)">2-5 Persons</option>
                          <option value="Team / Batch (6-15 Persons)">6-15 Persons</option>
                          <option value="Corporate / University (15+ Persons)">15+ Persons</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {/* IT Adaptive Sub-Form */}
                {isIT && (
                  <div className="p-3.5 bg-white/5 border border-white/10 rounded-xl space-y-3 animate-fadeIn">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-primary">
                      💻 IT Scope
                    </p>
                    <div>
                      <label className="block text-[11px] text-slate-300 mb-1">Requirement</label>
                      <select
                        value={form.itRequirement}
                        onChange={update("itRequirement")}
                        className="w-full px-3 py-1.5 bg-[#101c2a] border border-white/20 rounded-lg text-xs text-white focus:border-primary"
                      >
                        <option value="Custom Software Development">Custom Software Development</option>
                        <option value="Systems Architecture & Consultancy">Systems Architecture</option>
                        <option value="API Integration & Microservices">API Integration</option>
                        <option value="Network Infrastructure Setup">Network Infrastructure</option>
                        <option value="System Diagnostics & AMC Contract">System AMC Contract</option>
                      </select>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-[11px] text-slate-300 mb-1">Budget</label>
                        <select
                          value={form.budgetRange}
                          onChange={update("budgetRange")}
                          className="w-full px-2 py-1.5 bg-[#101c2a] border border-white/20 rounded-lg text-xs text-white focus:border-primary"
                        >
                          <option value="< ₹25,000 (Micro)">&lt; ₹25,000</option>
                          <option value="₹25,000 – ₹50,000">₹25,000 – ₹50,000</option>
                          <option value="₹50,000 – ₹1,00,000">₹50,000 – ₹1L</option>
                          <option value="₹1,00,000 – ₹2,50,000">₹1L – ₹2.5L</option>
                          <option value="₹2,50,000+ (Enterprise)">₹2.5L+</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-[11px] text-slate-300 mb-1">Timeline</label>
                        <select
                          value={form.timeline}
                          onChange={update("timeline")}
                          className="w-full px-2 py-1.5 bg-[#101c2a] border border-white/20 rounded-lg text-xs text-white focus:border-primary"
                        >
                          <option value="Immediate (< 2 weeks)">&lt; 2 Weeks</option>
                          <option value="1 Month">1 Month</option>
                          <option value="1 – 3 Months">1-3 Months</option>
                          <option value="Planning Phase">Planning</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {/* SaaS Adaptive Sub-Form */}
                {isSaaS && (
                  <div className="p-3.5 bg-white/5 border border-white/10 rounded-xl space-y-3 animate-fadeIn">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-primary">
                      ☁️ SaaS Product Specification
                    </p>
                    <div>
                      <label className="block text-[11px] text-slate-300 mb-1">Platform Type</label>
                      <select
                        value={form.platformType}
                        onChange={update("platformType")}
                        className="w-full px-3 py-1.5 bg-[#101c2a] border border-white/20 rounded-lg text-xs text-white focus:border-primary"
                      >
                        <option value="Multi-Tenant SaaS Product">Multi-Tenant SaaS Product</option>
                        <option value="CMS-Driven Web Application">CMS-Driven Web Application</option>
                        <option value="E-Commerce Storefront">E-Commerce Storefront</option>
                        <option value="Mobile App Backend API">Mobile App Backend API</option>
                        <option value="Custom Web Portal">Custom Web Portal</option>
                      </select>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-[11px] text-slate-300 mb-1">Budget</label>
                        <select
                          value={form.budgetRange}
                          onChange={update("budgetRange")}
                          className="w-full px-2 py-1.5 bg-[#101c2a] border border-white/20 rounded-lg text-xs text-white focus:border-primary"
                        >
                          <option value="₹25,000 – ₹50,000">₹25k – ₹50k</option>
                          <option value="₹50,000 – ₹1,00,000">₹50k – ₹1L</option>
                          <option value="₹1,00,000 – ₹2,50,000">₹1L – ₹2.5L</option>
                          <option value="₹2,50,000+ (Enterprise)">₹2.5L+</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-[11px] text-slate-300 mb-1">Timeline</label>
                        <select
                          value={form.timeline}
                          onChange={update("timeline")}
                          className="w-full px-2 py-1.5 bg-[#101c2a] border border-white/20 rounded-lg text-xs text-white focus:border-primary"
                        >
                          <option value="1 Month">1 Month</option>
                          <option value="1 – 3 Months">1-3 Months</option>
                          <option value="3 – 6 Months">3-6 Months</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {/* R&D Adaptive Sub-Form */}
                {isRnD && (
                  <div className="p-3.5 bg-white/5 border border-white/10 rounded-xl space-y-3 animate-fadeIn">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-primary">
                      🔬 R&D Specification
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-[11px] text-slate-300 mb-1">Focus Area</label>
                        <select
                          value={form.rndDomain}
                          onChange={update("rndDomain")}
                          className="w-full px-2 py-1.5 bg-[#101c2a] border border-white/20 rounded-lg text-xs text-white focus:border-primary"
                        >
                          <option value="Applied Hardware & IoT Prototyping">Hardware & IoT Prototyping</option>
                          <option value="Embedded Microcontroller & IoT Firmware">Embedded & IoT Firmware</option>
                          <option value="Sensor Telemetry & Signal Processing">Sensor Telemetry & IoT</option>
                          <option value="Circuit PCB Layout Design">PCB Layout Design</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-[11px] text-slate-300 mb-1">Stage</label>
                        <select
                          value={form.hardwareStage}
                          onChange={update("hardwareStage")}
                          className="w-full px-2 py-1.5 bg-[#101c2a] border border-white/20 rounded-lg text-xs text-white focus:border-primary"
                        >
                          <option value="Concept & Feasibility Study">Feasibility Study</option>
                          <option value="Schematic & Circuit Design">Schematic & Circuit</option>
                          <option value="Working Prototype Build">Prototype Build</option>
                          <option value="Testing & Debugging">Testing & Debug</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {/* Robotics Adaptive Sub-Form */}
                {isRobotics && (
                  <div className="p-3.5 bg-white/5 border border-white/10 rounded-xl space-y-3 animate-fadeIn">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-primary">
                      🤖 Robotics & Automation Specification
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-[11px] text-slate-300 mb-1">Target</label>
                        <select
                          value={form.automationTarget}
                          onChange={update("automationTarget")}
                          className="w-full px-2 py-1.5 bg-[#101c2a] border border-white/20 rounded-lg text-xs text-white focus:border-primary"
                        >
                          <option value="Industrial IoT (IIoT) & Automation">Industrial IoT (IIoT)</option>
                          <option value="Intelligent Robotics Assembly">Robotics Assembly</option>
                          <option value="Industrial Control & SCADA Systems">Industrial Control & SCADA</option>
                          <option value="Microcontroller Firmware">Microcontroller Firmware</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-[11px] text-slate-300 mb-1">Environment</label>
                        <select
                          value={form.operatingEnvironment}
                          onChange={update("operatingEnvironment")}
                          className="w-full px-2 py-1.5 bg-[#101c2a] border border-white/20 rounded-lg text-xs text-white focus:border-primary"
                        >
                          <option value="Commercial Facility">Commercial</option>
                          <option value="Industrial / Factory">Industrial / Factory</option>
                          <option value="R&D Lab / Educational">R&D Lab</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {/* Security Adaptive Sub-Form */}
                {isSecurity && (
                  <div className="p-3.5 bg-white/5 border border-white/10 rounded-xl space-y-3 animate-fadeIn">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-primary">
                      🛡️ Security Specification
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-[11px] text-slate-300 mb-1">Primary Need</label>
                        <select
                          value={form.securityNeed}
                          onChange={update("securityNeed")}
                          className="w-full px-2 py-1.5 bg-[#101c2a] border border-white/20 rounded-lg text-xs text-white focus:border-primary"
                        >
                          <option value="IP Surveillance & CCTV System">IP Surveillance</option>
                          <option value="Biometric Access Control">Biometric Access</option>
                          <option value="Network Firewall Infrastructure">Network Firewall</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-[11px] text-slate-300 mb-1">Facility Scale</label>
                        <select
                          value={form.facilityScale}
                          onChange={update("facilityScale")}
                          className="w-full px-2 py-1.5 bg-[#101c2a] border border-white/20 rounded-lg text-xs text-white focus:border-primary"
                        >
                          <option value="Small Office / Retail">Small Office</option>
                          <option value="Multi-Storey Commercial Building">Commercial Building</option>
                          <option value="Industrial Plant / Campus">Industrial Plant</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {/* Custom Scope Sub-Form */}
                {isCustom && (
                  <div className="p-3.5 bg-white/5 border border-white/10 rounded-xl space-y-3 animate-fadeIn">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-primary">
                      ⚙️ Custom Scope
                    </p>
                    <div>
                      <label className="block text-[11px] text-slate-300 mb-1">Focus Area</label>
                      <select
                        value={form.customScopeCategory}
                        onChange={update("customScopeCategory")}
                        className="w-full px-3 py-1.5 bg-[#101c2a] border border-white/20 rounded-lg text-xs text-white focus:border-primary"
                      >
                        <option value="Custom Hardware + Software Hybrid">Hardware + Software Hybrid</option>
                        <option value="Enterprise Infrastructure Overhaul">Enterprise Infrastructure</option>
                        <option value="Specialized Technical Support / AMC">Technical AMC Support</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* 5. Shared Message Textarea */}
                <div>
                  <label className="block text-[11px] font-bold text-slate-300 uppercase tracking-wider mb-1">
                    {isTraining
                      ? "Learning Goals / Additional Context *"
                      : "Project Details / Requirements *"}
                  </label>
                  <textarea
                    rows={3}
                    required
                    value={form.message}
                    onChange={update("message")}
                    placeholder={
                      isTraining
                        ? "Mention your technical background, goals, or preferred schedule..."
                        : "Describe project requirements, features, or technical goals..."
                    }
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-xs text-white placeholder-slate-400 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all resize-none"
                  />
                </div>

                {errorMsg && <p className="text-red-400 text-xs font-medium">{errorMsg}</p>}

                <div className="flex gap-2 pt-1">
                  <button
                    type="submit"
                    disabled={status === "sending"}
                    className="flex-1 bg-primary hover:bg-sky-400 disabled:opacity-50 text-slate-950 font-bold text-xs py-2.5 px-3 rounded-xl transition-all shadow-lg shadow-primary/20 flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    {status === "sending" ? "Radie Processing..." : "Send Inquiry to Radie →"}
                  </button>
                  <a
                    href={buildWhatsappText()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs p-2.5 rounded-xl transition-all shadow-md flex items-center justify-center"
                    title="Send pre-filled form directly via WhatsApp"
                  >
                    <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                      <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
                    </svg>
                  </a>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Popover Action Menu */}
      {menuOpen && !chatOpen && (
        <div className="bg-[#101c2a] text-white rounded-2xl shadow-2xl border border-white/20 p-4 w-72 mb-1 animate-in">
          <div className="flex items-center justify-between pb-3 mb-3 border-b border-white/10">
            <span className="text-xs font-bold text-primary uppercase tracking-wider">
              Quick Connect & Tools
            </span>
            <button
              onClick={() => setMenuOpen(false)}
              className="text-slate-400 hover:text-white text-sm font-bold p-1 cursor-pointer"
            >
              ✕
            </button>
          </div>

          <div className="space-y-2">
            {/* Open Radie Lead Generation Chat */}
            <button
              onClick={() => {
                setMenuOpen(false);
                setChatOpen(true);
              }}
              className="w-full flex items-center gap-3 p-2.5 rounded-xl bg-white/10 hover:bg-white/20 border border-white/10 text-white transition-colors text-left cursor-pointer"
            >
              <div className="w-8 h-8 rounded-lg bg-primary text-slate-950 flex items-center justify-center shrink-0 shadow-sm font-extrabold text-sm">
                R
              </div>
              <div>
                <p className="text-xs font-bold text-white">Chat with Radie</p>
                <p className="text-[11px] text-slate-300 font-medium">Adaptive Technical Assistant</p>
              </div>
            </button>

            {/* Contact Page Link */}
            <a
              href="/contact"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-3 p-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white transition-colors"
            >
              <div className="w-8 h-8 rounded-lg bg-brand text-white flex items-center justify-center shrink-0 shadow-sm border border-white/20">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                </svg>
              </div>
              <div className="text-left">
                <p className="text-xs font-bold text-white">Full Contact Desk</p>
                <p className="text-[11px] text-slate-400">Maps & phone numbers</p>
              </div>
            </a>

            {/* Document Print Tool (Shown only on document routes) */}
            {isDocumentPage && (
              <div className="pt-2 mt-2 border-t border-white/10 space-y-2">
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                  Document Print Options
                </p>
                <div className="flex gap-1.5">
                  {Object.keys(paperSizes).map((s) => (
                    <button
                      key={s}
                      onClick={() => setPaperSize(s)}
                      className={`flex-1 py-1 px-2 rounded text-xs font-medium border transition-colors ${
                        paperSize === s
                          ? "bg-primary text-slate-950 border-primary font-bold"
                          : "bg-white/10 text-slate-300 border-white/20 hover:bg-white/20"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
                <button
                  onClick={handlePrint}
                  className="w-full bg-primary hover:bg-sky-400 text-slate-950 font-bold text-xs py-2 rounded-lg transition-colors flex items-center justify-center gap-1.5 cursor-pointer shadow-sm"
                >
                  <Printer className="w-3.5 h-3.5 shrink-0" />
                  <span>Print Document ({paperSize})</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Floating Action Buttons Cluster */}
      <div className="flex items-center gap-3">
        {/* Radie Assistant Floating Trigger */}
        <button
          onClick={() => {
            setChatOpen(!chatOpen);
            setMenuOpen(false);
          }}
          className="group relative inline-flex items-center gap-2.5 bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-3 rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 cursor-pointer"
          aria-label="Open Radie Chat Assistant"
        >
          <span className="relative flex h-2.5 w-2.5">
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-300"></span>
          </span>
          <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
            <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
          </svg>
          <span className="hidden sm:inline font-bold text-xs tracking-wide">Chat with Radie</span>
        </button>

        {/* Quick Tools Trigger */}
        <button
          onClick={() => {
            setMenuOpen(!menuOpen);
            setChatOpen(false);
          }}
          className="bg-brand hover:bg-brand-dark text-white w-12 h-12 rounded-full shadow-xl flex items-center justify-center transition-all duration-200 hover:scale-105 cursor-pointer border border-white/20"
          aria-label="Open Quick Tools Menu"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 12.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 18.75a.75.75 0 110-1.5.75.75 0 010 1.5z" />
          </svg>
        </button>
      </div>
    </div>
  );
}
